import React, { useState, useEffect } from 'react';
import { Card, Select, Typography, Space, DatePicker, Tooltip, Empty, Spin, Radio, Switch, Tag, Tabs } from 'antd';
import { InfoCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { 
  Line, XAxis, YAxis, CartesianGrid, 
  ResponsiveContainer, Legend, ReferenceLine, Area,
  ComposedChart, Tooltip as RechartsTooltip
} from 'recharts';
import OTRDataService from '../utils/OTRDataService';
import { OTR_THRESHOLDS } from '../utils/OTRDataService';
import { toHrsMinsSecs } from '../../../libs/formatLib';
import otrDataProcessor from '../utils/OTRDataProcessor';

const { Title, Text } = Typography;
const { Option } = Select;

/**
 * Statistical Trip Variance Chart
 * Displays mean/average variance with standard deviation bands
 * across multiple dates for better visualization of trends
 * 
 * @param {Object} props - Component props
 * @param {string} props.selectedRoute - The selected route identifier
 * @param {Object} props.selectedTrip - The selected trip object with tripId and route
 * @param {string} props.title - Chart title
 * @param {number} props.defaultRange - Default date range in days
 * @param {Array} props.filteredData - Pre-filtered data from parent component (optional, falls back to fetching all data)
 */
const OTRStatisticalVarianceChart = ({
  selectedTrip,
  title = "Trip Variance Statistical Analysis",
  filteredData = null // Accept filtered data as prop
}) => {
  const [data, setData] = useState([]);
  const [statisticalData, setStatisticalData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('mean'); // 'mean' or 'median'
  const [confidenceInterval, setConfidenceInterval] = useState(0.95); // 95% confidence interval
  const [showTimeOfDay, setShowTimeOfDay] = useState(false);
  const [reportData, setReportData] = useState(null);

  // Generate sequence numbers for stops
  const generateStopSequence = (data, maxStops = 50) => {
    if (!data || data.length === 0) return [];
    
    // First try to use any existing sequence data (STOP_SEQUENCE, TIME_RECORD_SEQUENCE, etc.)
    const sequenceField = data[0]?.STOP_SEQUENCE !== undefined ? 'STOP_SEQUENCE' : 
                        data[0]?.TIME_RECORD_SEQUENCE !== undefined ? 'TIME_RECORD_SEQUENCE' :
                        data[0]?.SEQUENCE_NUMBER !== undefined ? 'SEQUENCE_NUMBER' : null;
    
    if (sequenceField) {
      // Extract unique stop sequence numbers
      const stopSequences = [...new Set(
        data.map(item => Number(item[sequenceField]))
          .filter(seq => !isNaN(seq) && seq > 0 && seq <= maxStops)
      )];
      
      // Sort numerically
      return stopSequences.sort((a, b) => a - b);
    }
    
    // If we don't have sequence numbers, create sequence based on scheduled times
    const sortedData = [...data].sort((a, b) => {
      const timeA = a.SCHEDULED_ARRIVAL_TIME || a.SCHEDULED_DEPARTURE_TIME || 0;
      const timeB = b.SCHEDULED_ARRIVAL_TIME || b.SCHEDULED_DEPARTURE_TIME || 0;
      return timeA - timeB;
    });
    
    // Create artificial sequence numbers
    return Array.from(Array(sortedData.length).keys()).map(i => i + 1);
  };

  // Convert seconds to minutes for display
  const secondsToMinutes = (seconds) => {
    if (seconds === null || seconds === undefined) return null;
    return Math.floor(seconds / 60);
  };

  // Convert seconds to time string for display
  const secondsToTime = (seconds) => {
    if (seconds === null || seconds === undefined) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // Process and prepare statistical data
  const processStatisticalData = (inputData) => {
    setLoading(true);
    
    try {
      console.log("Processing statistical data with", inputData.length, "records");
      
      // Debug variance data availability
      const varianceFields = [
        'DEPARTURE_TIME_VARIANCE', 
        'departure_variance', 
        'ARRIVAL_TIME_VARIANCE', 
        'arrival_variance', 
        'variance'
      ];
      
      const hasVarianceData = inputData.some(item => 
        varianceFields.some(field => 
          item[field] !== undefined && item[field] !== null && typeof item[field] === 'number'
        )
      );
      
      console.log("Data has variance fields:", hasVarianceData);
      
      if (inputData.length > 0) {
        const sampleRecord = inputData[0];
        console.log("Sample record:", sampleRecord);
        console.log("Available fields:", Object.keys(sampleRecord));
        console.log("Variance fields in sample:", 
          varianceFields.map(field => ({ field, value: sampleRecord[field], type: typeof sampleRecord[field] }))
        );
        console.log("Sequence fields in sample:",
          ['STOP_SEQUENCE', 'TIME_RECORD_SEQUENCE', 'SEQUENCE_NUMBER'].map(field => 
            ({ field, value: sampleRecord[field], type: typeof sampleRecord[field] })
          )
        );
      }
      
      // If no data after filtering, return
      if (inputData.length === 0) {
        setStatisticalData([]);
        setLoading(false);
        return;
      }
      
      // Generate stop sequence
      const stopSequence = generateStopSequence(inputData);
      
      console.log("Generated stop sequence:", stopSequence);
      
      // Calculate statistics by stop sequence
      const statisticsByStop = {};
      
      // Group records by stop sequence
      inputData.forEach(item => {
        // Try different possible sequence fields
        const sequence = item.STOP_SEQUENCE !== undefined ? Number(item.STOP_SEQUENCE) :
                       item.TIME_RECORD_SEQUENCE !== undefined ? Number(item.TIME_RECORD_SEQUENCE) :
                       item.SEQUENCE_NUMBER !== undefined ? Number(item.SEQUENCE_NUMBER) : null;
        
        if (sequence === null) {
          console.log("Missing sequence number in record:", item);
          return;
        }
        
        if (!statisticsByStop[sequence]) {
          statisticsByStop[sequence] = {
            values: [],
            scheduledTimes: [] // Add array to store scheduled times
          };
        }
        
        // Use centralized processor for variance calculation
        const variance = otrDataProcessor.getDepartureVariance(item);
        
        // Store scheduled and actual times if available
        const scheduledTime = item.SCHEDULED_ARRIVAL_TIME || item.SCHEDULED_DEPARTURE_TIME || null;
        const actualTime = item.ACTUAL_ARRIVAL_TIME || item.ACTUAL_DEPARTURE_TIME || null;
        
        if (scheduledTime !== null) {
          statisticsByStop[sequence].scheduledTimes.push(scheduledTime);
        }
        
        if (actualTime !== null) {
          if (!statisticsByStop[sequence].actualTimes) {
            statisticsByStop[sequence].actualTimes = [];
          }
          statisticsByStop[sequence].actualTimes.push(actualTime);
        }
        
        // If we found a variance value, add it to the sequence group
        if (variance !== null && typeof variance === 'number' && !isNaN(variance)) {
          statisticsByStop[sequence].values.push(variance);
        }
      });
      
      // Calculate statistics for each stop
      Object.keys(statisticsByStop).forEach(seq => {
        const values = statisticsByStop[seq].values;
        const scheduledTimes = statisticsByStop[seq].scheduledTimes;
        const actualTimes = statisticsByStop[seq].actualTimes || [];
        
        if (values.length === 0) {
          console.log("No variance values for stop sequence", seq);
          return;
        }
        
        // Calculate mean
        const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
        
        // Calculate standard deviation
        const squaredDifferences = values.map(val => Math.pow(val - mean, 2));
        const variance = squaredDifferences.reduce((sum, val) => sum + val, 0) / values.length;
        const stdDev = Math.sqrt(variance);
        
        // Calculate median
        const sortedValues = [...values].sort((a, b) => a - b);
        const median = sortedValues.length % 2 === 0 
          ? (sortedValues[sortedValues.length / 2 - 1] + sortedValues[sortedValues.length / 2]) / 2
          : sortedValues[Math.floor(sortedValues.length / 2)];
        
        // Calculate average scheduled time
        const avgScheduledTime = scheduledTimes.length > 0 
          ? scheduledTimes.reduce((sum, time) => sum + time, 0) / scheduledTimes.length
          : null;
        
        // Calculate average actual time
        const avgActualTime = actualTimes.length > 0 
          ? actualTimes.reduce((sum, time) => sum + time, 0) / actualTimes.length
          : null;
        
        // Calculate confidence interval multiplier (z-score)
        let zScore;
        if (confidenceInterval === 0.99) zScore = 2.576;
        else if (confidenceInterval === 0.90) zScore = 1.645;
        else zScore = 1.96; // Default 95%
        
        const upperBound = mean + (zScore * stdDev);
        const lowerBound = mean - (zScore * stdDev);
        
        // Store calculated statistics
        statisticsByStop[seq].mean = mean;
        statisticsByStop[seq].median = median;
        statisticsByStop[seq].stdDev = stdDev;
        statisticsByStop[seq].upperBound = upperBound;
        statisticsByStop[seq].lowerBound = lowerBound;
        statisticsByStop[seq].sampleSize = values.length;
        statisticsByStop[seq].scheduledTimeOfDay = avgScheduledTime !== null ? secondsToMinutes(avgScheduledTime) : null;
        statisticsByStop[seq].actualTimeOfDay = avgActualTime !== null ? secondsToMinutes(avgActualTime) : null;
        // Keep backwards compatibility
        statisticsByStop[seq].timeOfDay = avgScheduledTime !== null ? secondsToMinutes(avgScheduledTime) : null;
      });
      
      // Prepare chart data
      const sequences = stopSequence.length > 0 ? stopSequence : 
                      Object.keys(statisticsByStop).map(Number).sort((a, b) => a - b);
      
      // Create chart data array
      const chartData = sequences.map(seq => {
        const stats = statisticsByStop[seq] || { 
          mean: 0, median: 0, stdDev: 0, upperBound: 0, lowerBound: 0, sampleSize: 0, timeOfDay: null
        };
        
        return {
          stopSequence: seq,
          mean: stats.mean || 0,
          median: stats.median || 0,
          upperBound: stats.upperBound || 0,
          lowerBound: stats.lowerBound || 0,
          stdDev: stats.stdDev || 0,
          sampleSize: stats.sampleSize || 0,
          timeOfDay: stats.timeOfDay,
          scheduledTimeOfDay: stats.scheduledTimeOfDay,
          actualTimeOfDay: stats.actualTimeOfDay
        };
      });
      
      console.log("Generated statistical chart data with", chartData.length, "points");
      
      // Filter out any points with no data
      const validChartData = chartData.filter(point => point.sampleSize > 0);
      
      if (validChartData.length === 0) {
        console.warn("No valid data points after processing");
      }
      
      setStatisticalData(validChartData);
    } catch (error) {
      console.error("Error processing statistical data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      console.log(`Getting trip data for trip ${selectedTrip.tripId}`);
      console.log(`Data source: ${filteredData ? 'Using provided filtered data' : 'Fetching from service'}`);
      
      let tripRecords = [];
      
      // Use filtered data if provided, otherwise fetch from service
      if (filteredData && Array.isArray(filteredData)) {
        console.log(`Using provided filtered data with ${filteredData.length} records`);
        // Filter for just the current trip from the provided filtered data
        tripRecords = filteredData.filter(r => (r.KEY || r.TRIP_ID) === selectedTrip.tripKey);
      } else {
        console.log(`No filtered data provided, fetching from service`);
        // Get raw data directly from data service (fallback)
        const allData = await OTRDataService.retrieveAllData();
        // Filter for just the current trip
        tripRecords = allData.filter(r => (r.KEY || r.TRIP_ID) === selectedTrip.tripKey);
      }
      
      console.log(`Sending ${tripRecords.length} records to OTRStatisticalVarianceChart`);

      if (tripRecords && tripRecords.length > 0) {
        console.log("Data received in OTRStatisticalVarianceChart:", tripRecords.length, "records");
        processStatisticalData(tripRecords);
        
        // Prepare report data for ProposedOTR
        const parsedData = tripRecords.map(record => ({
          route: selectedTrip.route,
          tripId: selectedTrip.tripId,
          stopId: record.TSN,
          stopName: record.STOP_NAME,
          scheduledTime: record.SCHEDULED_ARRIVAL_TIME ? 
            `${record.DATE_ID} ${secondsToTime(record.SCHEDULED_ARRIVAL_TIME)}` : record.SCHEDULED_DEPARTURE_TIME ?
            `${record.DATE_ID} ${secondsToTime(record.SCHEDULED_DEPARTURE_TIME)}` : null,
          actualTime: record.ACTUAL_ARRIVAL_TIME ? 
            `${record.DATE_ID} ${secondsToTime(record.ACTUAL_ARRIVAL_TIME)}` : record.ACTUAL_DEPARTURE_TIME ?
            `${record.DATE_ID} ${secondsToTime(record.ACTUAL_DEPARTURE_TIME)}` : null,
          varianceInSeconds: otrDataProcessor.getDepartureVariance(record),
          isTimingPoint: record.IS_TIMING_POINT === 'Y',
          tripDate: record.DATE_ID
        }));

        setReportData({
          parsedData,
          tripData: [{
            route: selectedTrip.route,
            tripId: selectedTrip.tripId,
            stops: parsedData
          }]
        });
      }
    }
    load().then(() => console.log("OTRStatisticalVarianceChart loaded"));
  }, [selectedTrip, filteredData]); // Add filteredData to dependency array

  // Handle view mode change
  const handleViewModeChange = (e) => {
    setViewMode(e.target.value);
  };
  
  // Handle confidence interval change
  const handleConfidenceIntervalChange = (value) => {
    setConfidenceInterval(parseFloat(value));
  };
  
  // Get domain for time of day
  const getTimeDomain = () => {
    const scheduledTimes = statisticalData.map(d => d.scheduledTimeOfDay).filter(t => t !== null);
    const actualTimes = statisticalData.map(d => d.actualTimeOfDay).filter(t => t !== null);
    const allTimes = [...scheduledTimes, ...actualTimes];
    
    if (allTimes.length === 0) return [0, 1440]; // 24 hours in minutes
    
    const minTime = Math.min(...allTimes);
    const maxTime = Math.max(...allTimes);
    
    // Add some padding (30 minutes)
    return [
      Math.max(0, minTime - 30),
      Math.min(1440, maxTime + 30)
    ];
  };

  // Format the value based on the current view
  const formatValue = (value) => {
    if (showTimeOfDay) {
      return secondsToTime(value * 60); // Convert minutes back to seconds for display
    }
    return toHrsMinsSecs(value);
  };

  // Custom tooltip to show detailed stop information
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const stopData = payload[0].payload;
      const centralMeasure = viewMode === 'mean' ? stopData.mean : stopData.median;
      
      // Determine status color based on central measure
      let color = 'green';
      let statusText = 'On Time';
      let icon = <CheckCircleOutlined />;
      
      if (centralMeasure <= OTR_THRESHOLDS.early) {
        color = 'red';
        statusText = 'Early';
        icon = <WarningOutlined />;
      } else if (centralMeasure >= OTR_THRESHOLDS.late) {
        color = 'blue';
        statusText = 'Late';
        icon = <CloseCircleOutlined />;
      }
      
      return (
        <Card size="small" style={{ border: `1px solid ${color}` }}>
          <Space direction="vertical" size="small">
            <Text strong>Stop {stopData.stopSequence}</Text>
            
            {/* Time of Day Information */}
            {stopData.scheduledTimeOfDay && (
              <Text>
                Scheduled Time: {secondsToTime(stopData.scheduledTimeOfDay * 60)}
              </Text>
            )}
            {stopData.actualTimeOfDay && (
            <Text>
                Actual Time: {secondsToTime(stopData.actualTimeOfDay * 60)}
            </Text>
            )}
            
            {/* Variance Information */}
            <Text>
              {viewMode === 'mean' ? 'Mean' : 'Median'} Variance: 
              <Text style={{ color }}>
                {centralMeasure > 0 ? ' +' : ' '}{toHrsMinsSecs(centralMeasure)}
              </Text>
            </Text>
            <Text>Standard Deviation: ±{toHrsMinsSecs(stopData.stdDev)}</Text>
            
            {/* Common Information */}
            <Text>Sample Size: {stopData.sampleSize} trips</Text>
            <Text>Confidence Interval: {confidenceInterval * 100}%</Text>
            <Tag color={color} icon={icon}>{statusText}</Tag>
          </Space>
        </Card>
      );
    }
    
    return null;
  };

  // Render statistical chart
  const renderChart = () => {
    if (loading) {
      return <div style={{ height: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Spin size="large" /></div>;
    }
    
    if (!statisticalData || statisticalData.length === 0) {
      return <Empty description="No data available" style={{ height: 400, display: 'flex', flexDirection: 'column', justifyContent: 'center' }} />;
    }
    
    const centralMeasure = viewMode === 'mean' ? 'mean' : 'median';
    
    // Calculate YAxis ticks at every minute mark
    const variances = statisticalData.flatMap(d => [d.upperBound, d.lowerBound, d[centralMeasure]]).filter(v => typeof v === 'number');
    const minVar = Math.min(...variances);
    const maxVar = Math.max(...variances);
    const minTick = Math.floor(minVar / 60) * 60;
    const maxTick = Math.max(Math.ceil(maxVar / 60) * 60, 120); // Ensure at least 2m (120s)
    const ticks = [];
    for (let t = minTick; t <= maxTick; t += 60) {
      ticks.push(t);
    }
    
    return (
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart
          data={statisticalData}
          margin={{ top: 20, right: 30, left: 5, bottom: 40 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="stopSequence" 
            label={{ value: 'Stop Sequence', position: 'insideBottomRight', offset: 0 }}
          />
          <YAxis 
            label={{ 
              value: showTimeOfDay ? 'Time of Day' : 'Variance', 
              angle: -90, 
              position: 'insideLeft',
              offset: 0
            }}
            tickFormatter={formatValue}
            ticks={showTimeOfDay ? undefined : ticks}
            domain={showTimeOfDay ? getTimeDomain() : [minTick, maxTick]}
            type="number"
          />
          <RechartsTooltip content={<CustomTooltip />} />
          <Legend />
          
          {/* Central Measure Line (Mean or Median) or Time of Day Lines */}
          {showTimeOfDay ? (
            <>
              {/* Scheduled Time Line */}
              <Line 
                type="monotone" 
                dataKey="scheduledTimeOfDay" 
                stroke="#52c41a" 
                strokeWidth={2} 
                strokeDasharray="5 5"
                dot={{ r: 3 }} 
                activeDot={{ r: 6 }} 
                name="Scheduled Time" 
              />
              {/* Actual Time Line */}
              <Line 
                type="monotone" 
                dataKey="actualTimeOfDay" 
                stroke="#1890ff" 
                strokeWidth={3} 
                dot={{ r: 4 }} 
                activeDot={{ r: 8 }} 
                name="Actual Time" 
              />
            </>
          ) : (
          <Line 
            type="monotone" 
              dataKey={centralMeasure} 
            stroke="#8884d8" 
            strokeWidth={3} 
            dot={{ r: 4 }} 
            activeDot={{ r: 8 }} 
              name={viewMode === 'mean' ? 'Mean' : 'Median'} 
          />
          )}
          
          {/* Reference Lines - only show when showing variance */}
          {!showTimeOfDay && (
            <>
              {/* Standard Deviation Band */}
              <Area
                type="monotone"
                dataKey="upperBound"
                stroke="none"
                fillOpacity={0.2}
                fill="#8884d8"
              />
              <Area
                type="monotone"
                dataKey="lowerBound"
                stroke="none"
                fillOpacity={0.2}
                fill="#0084d8"
              />

              <ReferenceLine
                y={OTR_THRESHOLDS.early}
                stroke="red"
                strokeDasharray="3 3"
                label={{ value: 'Early', position: 'insideTopRight', fill: 'red' }}
              />
              <ReferenceLine
                y={OTR_THRESHOLDS.late}
                stroke="blue"
                strokeDasharray="3 3"
                label={{ value: 'Late', position: 'insideBottomRight', fill: 'blue' }}
              />
            </>
          )}
        </ComposedChart>
      </ResponsiveContainer>
    );
  };
  
  return (
    <Card
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={4} style={{ margin: 0 }}>{title}</Title>
        </div>
      }
      extra={
        <Space>
          <Tooltip title="This chart displays statistical analysis of trip variance data across multiple dates. It shows the mean/median with standard deviation bands to represent data variability.">
            <InfoCircleOutlined style={{ fontSize: 16 }} />
          </Tooltip>
        </Space>
      }
    >
      <div style={{ marginBottom: 20 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space wrap>
            <Text strong>View Mode:</Text>
            <Radio.Group value={viewMode} onChange={handleViewModeChange}>
              <Radio.Button value="mean">Mean</Radio.Button>
              <Radio.Button value="median">Median</Radio.Button>
            </Radio.Group>
            
            <Text strong>Confidence:</Text>
            <Select 
              value={confidenceInterval} 
              onChange={handleConfidenceIntervalChange}
              style={{ width: 100 }}
            >
              <Option value={0.90}>90%</Option>
              <Option value={0.95}>95%</Option>
              <Option value={0.99}>99%</Option>
            </Select>

            <Text strong>Show Time of Day:</Text>
            <Switch 
              checked={showTimeOfDay}
              onChange={setShowTimeOfDay}
            />
          </Space>
          
          <Text type="secondary">
            {viewMode === 'mean' ? 'Showing mean values with standard deviation bands' : 'Showing median values with standard deviation bands'}.
            {` Filtered by main dashboard filters. Confidence interval: ${confidenceInterval * 100}%`}
          </Text>
        </Space>
      </div>
      
      {renderChart()}
      
      <div style={{ marginTop: 16 }}>
        <Text type="secondary">
          <strong>Note:</strong> This chart shows the {viewMode === 'mean' ? 'mean (average)' : 'median'} variance across all trips with 
          {` ${confidenceInterval * 100}% `}
          confidence interval bands (±{viewMode === 'mean' && statisticalData[0]?.stdDev ? statisticalData[0].stdDev.toFixed(1) : '0'} minutes).
          Performance is considered on-time between {(OTR_THRESHOLDS.early/60).toFixed(0)} (early) and {(OTR_THRESHOLDS.late/60).toFixed(0)} (late).
        </Text>
      </div>
    </Card>
  );
};

export default OTRStatisticalVarianceChart; 